
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  CreditCard, 
  Shield, 
  Users, 
  BarChart3,
  ArrowRight,
  CheckCircle
} from "lucide-react";

/**
 * Landing page component
 * Showcases Coordi features and benefits
 * Provides navigation to login/register for new users
 * Serves as marketing/introduction page
 */
const Index = () => {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is authenticated
  useEffect(() => {
    // Check for existing session first
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        // User is authenticated, redirect to dashboard
        navigate("/dashboard", { replace: true });
      } else {
        // User not authenticated, redirect to login screen
        navigate("/login", { replace: true });
      }
    }).finally(() => {
      setCheckingAuth(false);
    });
  }, [navigate]);

  // Show loading spinner while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const features = [
    {
      icon: <CreditCard className="h-6 w-6" />,
      title: "Transaction Management",
      description: "Comprehensive tools to manage complex business transactions from start to finish."
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Compliance & Security", 
      description: "Built-in compliance tracking and enterprise-grade security for all your sensitive data."
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Collaboration Tools",
      description: "Seamless communication and collaboration features for all transaction contacts."
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Analytics & Reporting",
      description: "Powerful insights and customizable reports to track performance and bottlenecks."
    }
  ];

  const benefits = [
    "Streamline complex transaction workflows",
    "Reduce processing time by up to 60%", 
    "Ensure compliance with automated checks",
    "Centralize all transaction documents",
    "Real-time collaboration and updates",
    "Comprehensive audit trails"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Coordi</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/invite-required">
              <Button variant="ghost">Have an invite?</Button>
            </Link>
            <Link to="/invite-required">
              <Button>Request Access</Button>
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Streamline Your Business
            <span className="text-blue-600"> Transactions</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Coordi is the comprehensive platform for managing complex business transactions, 
            from initial documentation to final completion. Collaborate seamlessly, ensure compliance, 
            and track progress in real-time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/invite-required">
              <Button size="lg" className="text-lg px-8 py-3">
                Request Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button size="lg" variant="outline" className="text-lg px-8 py-3">
                View Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Everything You Need for Transaction Management
          </h3>
          <p className="text-lg text-gray-600">
            Powerful features designed to simplify complex business processes
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                  {feature.icon}
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Why Choose Coordi?
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Join thousands of businesses that have transformed their transaction 
                management processes with our comprehensive platform.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-8 text-white">
              <h4 className="text-2xl font-bold mb-4">Ready to Get Started?</h4>
              <p className="text-blue-100 mb-6">
                Experience the power of streamlined transaction management. 
                Start your free trial today and see the difference.
              </p>
              <div className="space-y-3">
                <Link to="/invite-required">
                  <Button size="lg" variant="secondary" className="w-full">
                    Request Access
                  </Button>
                </Link>
                <Link to="/invite-required">
                  <Button size="lg" variant="outline" className="w-full text-white border-white hover:bg-white hover:text-blue-600">
                    I have an invite link
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-6 w-6" />
                <span className="text-lg font-bold">Coordi</span>
              </div>
              <p className="text-gray-400">
                Streamlining business transactions for modern enterprises.
              </p>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Product</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
                <li><a href="#" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Company</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Support</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Documentation</a></li>
                <li><a href="#" className="hover:text-white">API Reference</a></li>
                <li><a href="#" className="hover:text-white">Community</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Coordi. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
